import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Mountain, Plus, Trash2, X, TrendingUp, Target, Wallet, Sparkles, Building2, ChevronRight, ArrowUp, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CLIMB } from "@/constants/testIds";

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

const money = (v) => {
  const n = Number(v || 0);
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: n < 100 ? 2 : 0,
    maximumFractionDigits: n < 100 ? 2 : 0,
  })}`;
};

const emptyForm = {
  name: "",
  account_id: "",
  starting_balance: "100",
  target_balance: "1000",
  risk_type: "percent",
  risk_value: "5",
  reward_multiple: "2",
  mode: "best_case",
  win_rate: "0.55",
  timeline_days: "",
};

/* -------------------------------------------------------------------------- */
/*  Accounts manager                                                          */
/* -------------------------------------------------------------------------- */

function AccountsManager({ accounts, onChange }) {
  const { t } = useTranslation();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ nickname: "", broker: "", account_number: "", starting_balance: "1000" });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nickname.trim()) return toast.error(t("climbUi.nicknameRequired"));
    if (!parseFloat(form.starting_balance)) return toast.error(t("climbUi.enterStartingBalance"));
    setSaving(true);
    try {
      await api.post("/accounts", {
        nickname: form.nickname.trim(),
        broker: form.broker.trim() || null,
        account_number: form.account_number.trim() || null,
        starting_balance: parseFloat(form.starting_balance),
      });
      toast.success(t("climbUi.accountAdded"));
      setForm({ nickname: "", broker: "", account_number: "", starting_balance: "1000" });
      setShowAdd(false);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("climbUi.couldNotAddAccount"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("climbUi.confirmDeleteAccount"))) return;
    try {
      await api.delete(`/accounts/${id}`);
      onChange();
    } catch {
      toast.error(t("climbUi.couldNotDeleteAccount"));
    }
  };

  return (
    <div
      data-testid={CLIMB.accountsManage}
      className="bg-white border border-slate-200 rounded-2xl p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Building2 size={16} className="text-blue-800" />
          <h3 className="font-display font-bold text-lg text-slate-900">{t("climbUi.tradingAccounts")}</h3>
          <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 ml-1">
            {accounts.length}
          </span>
        </div>
        <Button
          data-testid={CLIMB.accountAdd}
          variant="outline"
          size="sm"
          onClick={() => setShowAdd((v) => !v)}
          className="border-slate-300 h-8"
        >
          <Plus size={13} className="mr-1" /> {t("climbUi.addAccount")}
        </Button>
      </div>

      {showAdd && (
        <form onSubmit={submit} className="mb-4 grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
          <div className="col-span-2 sm:col-span-1">
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("climbUi.nickname")}</label>
            <Input value={form.nickname} onChange={(e) => set("nickname", e.target.value)} placeholder={t("climbUi.nicknamePlaceholder")} className="mt-1 h-9 bg-white" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("climbUi.broker")}</label>
            <Input value={form.broker} onChange={(e) => set("broker", e.target.value)} placeholder={t("climbUi.brokerPlaceholder")} className="mt-1 h-9 bg-white" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("climbUi.accountNumber")}</label>
            <Input value={form.account_number} onChange={(e) => set("account_number", e.target.value)} placeholder={t("climbUi.accountNumberPlaceholder")} className="mt-1 h-9 bg-white" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("climbUi.startingDollar")}</label>
            <Input type="number" inputMode="decimal" step="any" value={form.starting_balance} onChange={(e) => set("starting_balance", e.target.value)} className="mt-1 h-9 bg-white" />
          </div>
          <div className="col-span-2 sm:col-span-4 flex items-center justify-end gap-2">
            <button type="button" onClick={() => setShowAdd(false)} className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5">{t("climbUi.cancel")}</button>
            <Button type="submit" disabled={saving} size="sm" className="bg-blue-900 hover:bg-blue-800 text-white h-8">
              {saving ? t("climbUi.saving") : t("climbUi.saveAccount")}
            </Button>
          </div>
        </form>
      )}

      {accounts.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-slate-300 rounded-xl">
          <Building2 size={22} className="mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-600">
            {t("climbUi.noAccountsYet")}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {accounts.map((a) => (
            <div key={a.id} data-testid={CLIMB.accountRow} className="py-3 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-800 flex items-center justify-center shrink-0 font-bold">
                {(a.nickname || "?")[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 truncate">{a.nickname}</div>
                <div className="text-xs text-slate-500 truncate">
                  {[a.broker, a.account_number].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              <div className="font-mono text-sm text-slate-700 shrink-0">{money(a.starting_balance)}</div>
              <button
                data-testid={CLIMB.accountDelete}
                onClick={() => remove(a.id)}
                className="text-slate-400 hover:text-rose-500 transition-colors"
                aria-label={t("climbUi.deleteAccount")}
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  New plan form + live preview                                              */
/* -------------------------------------------------------------------------- */

function NewPlanForm({ accounts, onCreated, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyForm);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildPayload = () => ({
    name: form.name.trim(),
    account_id: form.account_id || null,
    starting_balance: parseFloat(form.starting_balance),
    target_balance: parseFloat(form.target_balance),
    risk_type: form.risk_type,
    risk_value: parseFloat(form.risk_value),
    reward_multiple: parseFloat(form.reward_multiple),
    win_rate: form.mode === "best_case" ? 1.0 : parseFloat(form.win_rate),
    mode: form.mode,
    timeline_days: form.timeline_days ? parseInt(form.timeline_days, 10) : null,
  });

  // Debounced preview refresh
  useEffect(() => {
    const p = buildPayload();
    if (!p.starting_balance || !p.target_balance || !p.risk_value || !p.reward_multiple) {
      setPreview(null);
      return;
    }
    if (p.target_balance <= p.starting_balance) {
      setPreview(null);
      return;
    }
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const r = await api.post("/climb/preview", { ...p, name: p.name || "Preview" });
        setPreview(r.data);
      } catch {
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [form.starting_balance, form.target_balance, form.risk_type, form.risk_value, form.reward_multiple, form.mode, form.win_rate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error(t("climbUi.giveYourPlanName"));
    if (!preview || !preview.trade_count) return toast.error(t("climbUi.fixInputsNoValidProjection"));
    setSaving(true);
    try {
      const res = await api.post("/climb/plans", buildPayload());
      toast.success(t("climbUi.planCreated"));
      onCreated(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("climbUi.couldNotCreatePlan"));
    } finally {
      setSaving(false);
    }
  };

  const pace = useMemo(() => {
    if (!preview?.trade_count || !form.timeline_days) return null;
    const days = parseInt(form.timeline_days, 10);
    if (!days) return null;
    const perTrade = days / preview.trade_count;
    return perTrade >= 1
      ? t("climbUi.oneTradeEveryDays", { days: perTrade.toFixed(1) })
      : t("climbUi.tradesPerDay", { trades: (1 / perTrade).toFixed(1) });
  }, [preview, form.timeline_days]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-6">
      {/* Form */}
      <form
        data-testid={CLIMB.formPreview}
        onSubmit={submit}
        className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4"
      >
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-700 to-blue-900 text-white flex items-center justify-center">
            <Mountain size={18} />
          </div>
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900">{t("climbUi.newClimbPlan")}</h3>
            <p className="text-xs text-slate-500">{t("climbUi.projectedPathDescription")}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("climbUi.planName")}</label>
            <Input
              data-testid={CLIMB.formName}
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder={t("climbUi.planNamePlaceholder")}
              className="mt-1 h-10 bg-white"
            />
          </div>

          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("climbUi.accountOptional")}</label>
            <select
              data-testid={CLIMB.formAccount}
              value={form.account_id}
              onChange={(e) => set("account_id", e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
            >
              <option value="">{t("climbUi.accountNone")}</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.nickname}{a.broker ? ` (${a.broker})` : ""}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("climbUi.startingBalance")}</label>
            <Input data-testid={CLIMB.formStart} type="number" inputMode="decimal" step="any" value={form.starting_balance} onChange={(e) => set("starting_balance", e.target.value)} className="mt-1 h-10 bg-white" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("climbUi.targetBalance")}</label>
            <Input data-testid={CLIMB.formTarget} type="number" inputMode="decimal" step="any" value={form.target_balance} onChange={(e) => set("target_balance", e.target.value)} className="mt-1 h-10 bg-white" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("climbUi.riskPerTrade")}</label>
              <div className="flex rounded-md bg-slate-100 p-0.5" data-testid={CLIMB.formRiskType}>
                {[["percent", "%"], ["dollar", "$"]].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => set("risk_type", v)}
                    className={`text-[10px] font-bold px-2 py-0.5 rounded transition-colors ${
                      form.risk_type === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="relative">
              <Input data-testid={CLIMB.formRiskValue} type="number" inputMode="decimal" step="any" value={form.risk_value} onChange={(e) => set("risk_value", e.target.value)} className="h-10 pr-8 bg-white" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-slate-400">
                {form.risk_type === "percent" ? "%" : "$"}
              </span>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("climbUi.rewardRisk")}</label>
            <div className="relative">
              <Input data-testid={CLIMB.formReward} type="number" inputMode="decimal" step="any" value={form.reward_multiple} onChange={(e) => set("reward_multiple", e.target.value)} className="mt-1 h-10 pr-8 bg-white" />
              <span className="absolute right-3 top-1/2 translate-y-0.5 text-xs font-mono text-slate-400">R</span>
            </div>
          </div>

          <div className="col-span-2">
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("climbUi.projectionMode")}</label>
              <div className="flex rounded-md bg-slate-100 p-0.5" data-testid={CLIMB.formMode}>
                {[
                  ["best_case", t("climbUi.bestCase")],
                  ["realistic", t("climbUi.realistic")],
                ].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => set("mode", v)}
                    className={`text-[10px] font-bold px-2.5 py-0.5 rounded transition-colors ${
                      form.mode === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {form.mode === "realistic" && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-semibold text-slate-500">
                  <span>{t("climbUi.assumedWinRate")}</span>
                  <span className="font-mono text-slate-700">{Math.round(parseFloat(form.win_rate) * 100)}%</span>
                </div>
                <input
                  data-testid={CLIMB.formWinRate}
                  type="range"
                  min="0.5"
                  max="0.95"
                  step="0.01"
                  value={form.win_rate}
                  onChange={(e) => set("win_rate", e.target.value)}
                  className="mt-2 w-full accent-blue-800"
                />
              </div>
            )}
          </div>

          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("climbUi.timelineDaysOptional")}</label>
            <Input data-testid={CLIMB.formTimeline} type="number" inputMode="numeric" value={form.timeline_days} onChange={(e) => set("timeline_days", e.target.value)} placeholder={t("climbUi.timelinePlaceholder")} className="mt-1 h-10 bg-white" />
          </div>
        </div>

        <div className="flex items-center gap-2 justify-end pt-2 border-t border-slate-100">
          <button
            data-testid={CLIMB.formCancel}
            type="button"
            onClick={onCancel}
            className="text-xs text-slate-500 hover:text-slate-700 px-3 py-2"
          >
            {t("climbUi.cancel")}
          </button>
          <Button
            data-testid={CLIMB.formSubmit}
            type="submit"
            disabled={saving}
            className="bg-blue-900 hover:bg-blue-800 text-white h-10 px-6"
          >
            {saving ? t("climbUi.saving") : t("climbUi.savePlan")}
          </Button>
        </div>
      </form>

      {/* Preview panel */}
      <div className="relative overflow-hidden rounded-2xl bg-slate-950 text-white p-6 border border-slate-800 min-h-[300px]">
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-500/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 font-semibold flex items-center gap-2">
            <Sparkles size={12} /> {t("climbUi.projection")}
            {previewLoading && <RefreshCw size={11} className="animate-spin text-slate-500" />}
          </div>

          {!preview || preview.trade_count === 0 ? (
            <p className="mt-6 text-sm text-slate-400 leading-relaxed">
              {t("climbUi.fillInputsToSeeProjection")}
            </p>
          ) : preview.ev_negative ? (
            <p className="mt-6 text-sm text-rose-300 leading-relaxed">
              {preview.message}
            </p>
          ) : (
            <>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display font-black text-5xl text-white tracking-tight">
                  {preview.trade_count}
                </span>
                <span className="text-slate-400 text-sm">{t("climbUi.tradesToTarget")}</span>
              </div>
              {pace && (
                <div className="text-xs text-amber-300 font-semibold mt-1">{pace}</div>
              )}

              <div className="grid grid-cols-3 gap-2 mt-4">
                <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                  <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">{t("climbUi.final")}</div>
                  <div className="font-mono font-bold text-white text-sm">{money(preview.final_balance)}</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                  <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">{t("climbUi.gain")}</div>
                  <div className="font-mono font-bold text-emerald-400 text-sm">
                    +{money(preview.final_balance - parseFloat(form.starting_balance || 0))}
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                  <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">{t("climbUi.mode")}</div>
                  <div className="font-mono font-bold text-amber-400 text-xs uppercase tracking-wider">
                    {form.mode === "best_case" ? t("climbUi.best") : `${Math.round(parseFloat(form.win_rate) * 100)}%W`}
                  </div>
                </div>
              </div>

              <div className="mt-5 border-t border-slate-800 pt-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
                  {t("climbUi.tradeByTradePath")}
                </div>
                <div className="max-h-56 overflow-y-auto pr-2 -mr-2 space-y-1">
                  {preview.trades.slice(0, 40).map((tr) => (
                    <div key={tr.trade_number} className="flex items-center gap-3 text-xs font-mono">
                      <span className="w-8 text-slate-500 tabular-nums">#{tr.trade_number}</span>
                      <span className="flex-1 text-slate-500">
                        {money(tr.balance_before)} <ArrowUp size={10} className="inline text-emerald-400" />{" "}
                        <span className="text-emerald-400">+{money(tr.gain_projected)}</span>
                      </span>
                      <span className="text-white font-bold tabular-nums shrink-0">{money(tr.balance_after)}</span>
                    </div>
                  ))}
                  {preview.trades.length > 40 && (
                    <div className="text-[10px] text-slate-500 italic pt-2">
                      {t("climbUi.showingFirst40", { total: preview.trades.length })}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Plan detail                                                               */
/* -------------------------------------------------------------------------- */

function PlanDetail({ plan, accounts, onClose, onChange }) {
  const { t } = useTranslation();
  const acct = accounts.find((a) => a.id === plan.account_id);
  const proj = plan.projection || {};
  const goalPct = Math.min(
    100,
    Math.max(0, ((plan.current_balance - plan.starting_balance) / (plan.target_balance - plan.starting_balance)) * 100),
  );

  const remove = async () => {
    if (!window.confirm(t("climbUi.confirmDeletePlan", { name: plan.name }))) return;
    try {
      await api.delete(`/climb/plans/${plan.id}`);
      toast.success(t("climbUi.planDeleted"));
      onClose();
      onChange();
    } catch {
      toast.error(t("climbUi.couldNotDelete"));
    }
  };

  return (
    <div
      data-testid={CLIMB.planDetail}
      className="relative bg-slate-950 text-white rounded-2xl border border-amber-500/25 overflow-hidden"
    >
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-amber-500/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />
      <div className="relative p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 font-semibold flex items-center gap-2 mb-2">
              <Mountain size={12} /> {t("climbUi.climbPlan")}
            </div>
            <h2 className="font-display font-black text-3xl tracking-tight leading-tight">{plan.name}</h2>
            {acct && (
              <div className="mt-1 text-xs text-slate-400">
                <Building2 size={11} className="inline -mt-0.5 mr-1" /> {acct.nickname}
                {acct.broker && ` · ${acct.broker}`}
                {acct.account_number && ` · #${acct.account_number}`}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid={CLIMB.planDelete}
              onClick={remove}
              className="text-slate-500 hover:text-rose-400 transition-colors"
              aria-label={t("climbUi.deletePlan")}
            >
              <Trash2 size={16} />
            </button>
            <button
              data-testid={CLIMB.planClose}
              onClick={onClose}
              className="text-slate-500 hover:text-white transition-colors"
              aria-label={t("climbUi.closePlanDetail")}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Progress */}
        <div data-testid={CLIMB.planProgress} className="mt-6">
          <div className="flex items-baseline justify-between text-xs text-slate-400 mb-2">
            <span>
              <span className="font-mono font-bold text-white text-base">{money(plan.current_balance)}</span>
              <span className="text-slate-500"> of {money(plan.target_balance)}</span>
            </span>
            <span className="font-mono text-amber-300">{goalPct.toFixed(1)}%</span>
          </div>
          <div className="h-3 rounded-full bg-white/10 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${
                goalPct >= 100 ? "bg-emerald-500" : "bg-gradient-to-r from-blue-500 to-amber-400"
              }`}
              style={{ width: `${goalPct}%` }}
            />
          </div>
          <div className="mt-3 grid grid-cols-4 gap-3">
            <Stat label={t("climbUi.start")} value={money(plan.starting_balance)} icon={Wallet} />
            <Stat
              label={t("climbUi.current")}
              value={money(plan.current_balance)}
              tone={plan.current_balance > plan.starting_balance ? "up" : plan.current_balance < plan.starting_balance ? "down" : null}
              icon={TrendingUp}
            />
            <Stat label={t("climbUi.target")} value={money(plan.target_balance)} icon={Target} />
            <Stat
              label={t("climbUi.remaining")}
              value={proj.reached_target ? "0" : proj.trade_count}
              subLabel={t("climbUi.trades")}
              icon={ArrowUp}
            />
          </div>
        </div>

        {/* Setup summary */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs bg-white/5 border border-white/10 rounded-xl p-4">
          <SummaryChip label={t("climbUi.risk")} value={plan.risk_type === "percent" ? `${plan.risk_value}%` : money(plan.risk_value)} />
          <SummaryChip label={t("climbUi.rewardRiskLabel")} value={`${plan.reward_multiple}R`} />
          <SummaryChip label={t("climbUi.mode")} value={plan.mode === "best_case" ? t("climbUi.bestCase") : t("climbUi.percentWin", { percent: Math.round(plan.win_rate * 100) })} />
          <SummaryChip label={t("climbUi.linkedTrades")} value={plan.linked_trade_ids?.length || 0} />
        </div>

        {/* Trades remaining */}
        <div data-testid={CLIMB.planTrades} className="mt-6">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2 flex items-center justify-between">
            <span>{proj.reached_target ? t("climbUi.youDidIt") : t("climbUi.remainingProjectedTrades")}</span>
            {!proj.reached_target && (
              <span className="text-amber-300 font-mono">
                {t("climbUi.toGo", { count: proj.trade_count })}
              </span>
            )}
          </div>
          {proj.reached_target ? (
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-6 text-center">
              <Sparkles size={24} className="mx-auto text-emerald-400 mb-2" />
              <div className="font-display font-bold text-lg text-emerald-300">{t("climbUi.targetReached")}</div>
              <div className="text-xs text-emerald-200/70 mt-1">
                {t("climbUi.considerRaisingTarget")}
              </div>
            </div>
          ) : proj.ev_negative ? (
            <div className="text-sm text-rose-300 py-4">{proj.message}</div>
          ) : (
            <div className="max-h-72 overflow-y-auto pr-2 -mr-2 space-y-1 rounded-xl bg-black/30 border border-white/5 p-3">
              {(proj.trades || []).slice(0, 60).map((tr) => (
                <div key={tr.trade_number} className="flex items-center gap-3 text-xs font-mono">
                  <span className="w-8 text-slate-500 tabular-nums">#{tr.trade_number}</span>
                  <span className="flex-1 text-slate-500">
                    {money(tr.balance_before)} <ArrowUp size={10} className="inline text-emerald-400" />{" "}
                    <span className="text-emerald-400">+{money(tr.gain_projected)}</span>
                  </span>
                  <span className="text-white font-bold tabular-nums shrink-0">{money(tr.balance_after)}</span>
                </div>
              ))}
              {(proj.trades || []).length > 60 && (
                <div className="text-[10px] text-slate-500 italic pt-2 text-center">
                  {t("climbUi.showingFirst60", { total: proj.trades.length })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, subLabel, tone, icon: Icon }) {
  const color =
    tone === "up" ? "text-emerald-400" :
    tone === "down" ? "text-rose-400" : "text-white";
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
      <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold flex items-center gap-1">
        {Icon && <Icon size={9} />} {label}
      </div>
      <div className={`font-mono font-bold ${color} mt-1 tabular-nums text-sm`}>
        {value}
        {subLabel && <span className="text-slate-500 font-normal ml-1 text-[10px]">{subLabel}</span>}
      </div>
    </div>
  );
}

function SummaryChip({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
      <div className="mt-0.5 font-mono font-bold text-white">{value}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Root                                                                      */
/* -------------------------------------------------------------------------- */

export default function Climb() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [openPlan, setOpenPlan] = useState(null);

  const loadAll = async () => {
    try {
      const [aRes, pRes] = await Promise.all([
        api.get("/accounts"),
        api.get("/climb/plans"),
      ]);
      setAccounts(aRes.data);
      setPlans(pRes.data);
    } catch {
      // Silently ignore — page will show empty state.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const refreshOpenPlan = async () => {
    if (!openPlan) return;
    try {
      const r = await api.get(`/climb/plans/${openPlan.id}`);
      setOpenPlan(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { refreshOpenPlan(); }, [plans.length]);

  return (
    <div data-testid={CLIMB.root} className="space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-900 via-slate-950 to-slate-900 text-white p-8 sm:p-10 border border-blue-800/50">
        <div className="absolute -top-24 right-8 w-64 h-64 rounded-full bg-amber-500/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-8 w-64 h-64 rounded-full bg-blue-500/25 blur-3xl pointer-events-none" />
        <div className="relative grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-6 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 font-semibold flex items-center gap-2 mb-3">
              <Mountain size={14} /> {t("climbUi.theClimb")}
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-tight">
              {t("climbUi.everyAscentStarts")}
            </h2>
            <p className="mt-3 text-sm text-slate-300 max-w-lg leading-relaxed">
              {t("climbUi.heroDescription")}
            </p>
          </div>
          <div className="flex md:justify-end">
            <Button
              data-testid={CLIMB.newPlanButton}
              onClick={() => setShowNew(true)}
              size="lg"
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-6"
            >
              <Plus size={16} className="mr-2" /> {t("climbUi.startNewClimb")}
            </Button>
          </div>
        </div>
      </div>

      {/* New plan form */}
      {showNew && (
        <NewPlanForm
          accounts={accounts}
          onCreated={(p) => {
            setShowNew(false);
            setPlans((prev) => [p, ...prev]);
            setOpenPlan(p);
          }}
          onCancel={() => setShowNew(false)}
        />
      )}

      {/* Open plan detail */}
      {openPlan && (
        <PlanDetail
          plan={openPlan}
          accounts={accounts}
          onClose={() => setOpenPlan(null)}
          onChange={loadAll}
        />
      )}

      {/* Plans list */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={16} className="text-blue-800" />
          <h3 className="font-display font-bold text-lg text-slate-900">{t("climbUi.yourPlans")}</h3>
          <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 ml-1">
            {plans.length}
          </span>
        </div>
        {loading ? (
          <div className="text-sm text-slate-500 py-6">{t("climbUi.loading")}</div>
        ) : plans.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-slate-300">
            <Mountain size={26} className="mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-600 font-semibold">{t("climbUi.noClimbPlansYet")}</p>
            <p className="text-xs text-slate-500 mt-1">{t("climbUi.clickStartNewClimb")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {plans.map((p) => {
              const acct = accounts.find((a) => a.id === p.account_id);
              const pct = Math.min(
                100,
                Math.max(0, ((p.current_balance - p.starting_balance) / (p.target_balance - p.starting_balance)) * 100),
              );
              const done = pct >= 100;
              return (
                <button
                  key={p.id}
                  data-testid={CLIMB.planCard}
                  onClick={() => setOpenPlan(p)}
                  className="text-left group relative bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300 transition-all"
                >
                  <div className="absolute top-4 right-4 text-slate-400 group-hover:text-blue-800">
                    <ChevronRight size={16} />
                  </div>
                  <h4 className="font-display font-bold text-lg text-slate-900 pr-6 leading-snug">{p.name}</h4>
                  {acct && (
                    <div className="mt-0.5 text-xs text-slate-500 truncate">
                      <Building2 size={10} className="inline -mt-0.5 mr-1" /> {acct.nickname}
                    </div>
                  )}
                  <div className="mt-4 flex items-baseline justify-between text-xs">
                    <span>
                      <span className="font-mono font-bold text-slate-900 text-base">{money(p.current_balance)}</span>
                      <span className="text-slate-400 ml-1">/ {money(p.target_balance)}</span>
                    </span>
                    <span className="font-mono text-amber-600 font-semibold">{pct.toFixed(0)}%</span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${done ? "bg-emerald-500" : "bg-gradient-to-r from-blue-500 to-amber-400"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-[10px] text-slate-500">
                    <span>{p.risk_type === "percent" ? `${p.risk_value}%` : money(p.risk_value)} {t("climbUi.riskLowercase")}</span>
                    <span>·</span>
                    <span>{p.reward_multiple}R</span>
                    <span>·</span>
                    <span>{p.mode === "best_case" ? t("climbUi.bestCase") : `${Math.round(p.win_rate * 100)}%W`}</span>
                    <span className="ml-auto text-slate-400">
                      {p.projection?.reached_target ? t("climbUi.complete") : t("climbUi.countToGo", { count: p.projection?.trade_count || "—" })}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Accounts manager */}
      <AccountsManager accounts={accounts} onChange={loadAll} />
    </div>
  );
}
